import type { OrgMember } from "@solomon-apps/shared";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import {
    Dialog,
    DialogContent,
    DialogTitle
} from "@solomon-apps/shared/src/components/ui/dialog";
import { FormActions } from "@solomon-apps/shared/src/components/ui/form-actions";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { CrossIcon } from "@solomon-labs/design/components/icons";
import { ButtonIcon } from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import React, { useState } from "react";

/**
 * Team-page confirmation dialog. One shared shape across all three actions:
 * an action-only title, a body that lists the target member (name / email /
 * role) under an intro line, an optional clarifying sentence, then "Are you
 * sure?", closed by the shared `FormActions` pair (confirm + Cancel). Close
 * affordance is the DS `ButtonIcon` + `CrossIcon`, sized as the DS
 * `DialogCloseButton` (transparent, lg, h-7 / md:h-8).
 *
 *   - `cancelInvitation`: withdraw a pending invite
 *   - `resendInvitation`: replace a pending invite and attempt delivery
 *   - `suspend`:          destructive, reversible
 *   - `reactivate`:       constructive, reversible
 *   - `resetPassword`:    issue a one-time password reset link
 */
export type TeamMemberActionKind =
    | "cancelInvitation"
    | "resendInvitation"
    | "suspend"
    | "reactivate"
    | "resetPassword";

interface TeamMemberActionDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    kind: TeamMemberActionKind;
    member: OrgMember | undefined;
    onConfirm: () => void;
    isSubmitting?: boolean;
    error?: Error | null;
    /**
     * Where focus lands on dismiss. The page opens this dialog from a ⋯ menu
     * item or a drawer button, never a Dialog trigger, so Base UI has no
     * trigger to return to and focus would fall to <body>.
     */
    finalFocus?: () => HTMLElement | boolean;
}

export interface DialogCopy {
    title: string;
    body: React.ReactNode;
    confirmLabel: string;
}

export function TeamMemberActionDialog({
    open,
    onOpenChange,
    kind,
    member,
    onConfirm,
    isSubmitting = false,
    error,
    finalFocus
}: TeamMemberActionDialogProps): React.ReactElement | null {
    // Latch the last member + kind while the dialog closes. The parent clears
    // its intent (and so `member`) the moment the dialog closes; unmounting
    // then would cut the popup's exit animation, and `kind` would flip
    // (Suspend to Reactivate) mid-fade.
    const [shown, setShown] = useState<{
        member: OrgMember;
        kind: TeamMemberActionKind;
    } | null>(open && member ? { member, kind } : null);
    if (open && member && (shown?.member !== member || shown.kind !== kind)) {
        setShown({ member, kind });
    }
    const reducedMotion = useReducedMotion() ?? false;
    const current = open && member ? { member, kind } : shown;
    if (!current) return null;

    const copy = buildCopy(current.kind, current.member);

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next && !isSubmitting) onOpenChange(false);
            }}
        >
            <DialogContent
                finalFocus={finalFocus}
                className="max-h-[88vh] max-w-[480px] gap-0 overflow-hidden p-0"
            >
                {/* Header */}
                <div className="border-b border-line-base px-6 pt-6 pb-4">
                    <div className="flex items-center justify-between gap-4">
                        {/* The catalog DialogTitle names the dialog
                            (aria-labelledby). */}
                        <DialogTitle variant="h2">{copy.title}</DialogTitle>
                        <ButtonIcon
                            variant="transparent"
                            size="lg"
                            icon={<CrossIcon aria-hidden="true" />}
                            aria-label="Close"
                            disabled={isSubmitting}
                            onClick={() => onOpenChange(false)}
                            className="-mr-1 h-7 w-7 shrink-0 md:h-8 md:w-8"
                        />
                    </div>
                </div>

                {/* Body */}
                <div className="overflow-y-auto px-6 py-5 flex flex-col gap-4">
                    {copy.body}
                    {error && (
                        // An error arriving after Confirm fades in: opacity
                        // only, 200ms ease-out-quad, skipped under reduced
                        // motion.
                        <motion.div
                            initial={reducedMotion ? false : { opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{
                                duration: 0.2,
                                ease: easings.outQuad
                            }}
                        >
                            <Text
                                variant="small"
                                render={<p />}
                                className="text-error-base"
                            >
                                {error.message}
                            </Text>
                        </motion.div>
                    )}
                </div>

                {/* Footer: side-by-side actions, no divider above. */}
                <div className="px-6 pt-8 pb-5">
                    <FormActions
                        primaryLabel={
                            isSubmitting ? "Working..." : copy.confirmLabel
                        }
                        onPrimary={onConfirm}
                        primaryDisabled={isSubmitting}
                        cancelLabel="Cancel"
                        onCancel={() => onOpenChange(false)}
                        cancelDisabled={isSubmitting}
                        size="md"
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ── Copy builder ───────────────────────────────────────────── */

export function buildCopy(
    kind: TeamMemberActionKind,
    member: OrgMember
): DialogCopy {
    switch (kind) {
        case "cancelInvitation":
            return {
                title: "Withdraw Invitation",
                body: confirmBody(
                    "Withdraws the pending invitation for:",
                    member
                ),
                confirmLabel: "Withdraw Invitation"
            };

        case "resendInvitation":
            return {
                title: "Resend Invitation",
                body: confirmBody(
                    "A new invitation link will be created for:",
                    member,
                    "The new link is valid for 7 days and the previous link stops working. If the email does not arrive, copy the new link."
                ),
                confirmLabel: "Resend Invitation"
            };

        case "suspend":
            return {
                title: "Suspend Member",
                body: confirmBody(
                    "Signs this member out and blocks console access until reactivated:",
                    member,
                    "Their role is kept. Reactivate them anytime."
                ),
                confirmLabel: "Suspend"
            };

        case "reactivate":
            return {
                title: "Reactivate Member",
                body: confirmBody(
                    "Restores console access immediately for:",
                    member,
                    `They'll sign back in with their previous role (${member.orgRole}).`
                ),
                confirmLabel: "Reactivate"
            };

        case "resetPassword":
            return {
                title: "Reset Password",
                body: confirmBody(
                    "Generates a one-time password reset link for:",
                    member,
                    "Their current password works until they set a new one."
                ),
                confirmLabel: "Reset Password"
            };
    }
}

/**
 * Shared confirmation body: intro line → member bullets (name / email / role)
 * → optional clarifying sentence → "Are you sure?".
 */
function confirmBody(
    intro: string,
    member: OrgMember,
    clarification?: string
): React.ReactNode {
    // Skip the name line when there's no full name (it would repeat the email).
    const lines = [
        ...(member.fullName ? [member.fullName] : []),
        member.email,
        member.orgRole
    ];
    return (
        <div className="flex flex-col gap-3 text-foreground-base">
            <Text variant="small" render={<p />}>
                {intro}
            </Text>
            <ul className="flex flex-col gap-1.5 pl-1">
                {lines.map((line) => (
                    <li key={line} className="flex items-center gap-2">
                        {/* IndicationUnit as the bullet marker, in olive
                            (tint, a supporting element): Ember is earned,
                            and three Ember bullets would compete with the
                            dialog shell's one accent. */}
                        <IndicationUnit size={6} color="tint" />
                        <Text variant="small" render={<span />}>
                            {line}
                        </Text>
                    </li>
                ))}
            </ul>
            {clarification && (
                <Text variant="small" render={<p />}>
                    {clarification}
                </Text>
            )}
            <Text variant="small" render={<p />}>
                Are you sure?
            </Text>
        </div>
    );
}
